import { useState } from 'react';

import { isApiError } from '../../../../shared/http/api-error.js';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULE_TEXT,
} from '../../../../shared/schema/auth.schema.js';
import { Field } from '../../../ui/Field.jsx';

/**
 * The sign-in and sign-up form.
 *
 * One component for both, because the fields and the failure handling are the same and
 * only the extra name field and the copy differ. The old app had a login form and a
 * four-step wizard that between them contained four components defined inside a render
 * body, so every keystroke remounted the input and destroyed its focus.
 *
 * Form state is local and committed on submit. The old wizard pushed every keystroke
 * into global state, which meant a PATCH per character and half-entered data persisted
 * if someone abandoned the form.
 *
 * The submit button is genuinely disabled until the form can be submitted. The old one
 * only dropped its opacity to 0.6 and stayed clickable, with required fields that were
 * never enforced.
 *
 * Audit references prevented here: auth.jsx #2, #3, #4, #8, #16, #18.
 *
 * @param {object} props
 * @param {'sign-in'|'sign-up'} props.mode
 * @param {(input: {email: string, password: string, displayName?: string}) => Promise<void>} props.onSubmit
 * @param {() => void} props.onSwitchMode
 */
export function CredentialsForm({ mode, onSubmit, onSwitchMode }) {
  const isSignUp = mode === 'sign-up';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const canSubmit =
    email.trim().length > 0 &&
    password.length >= (isSignUp ? PASSWORD_MIN_LENGTH : 1) &&
    (!isSignUp || displayName.trim().length > 0) &&
    !busy;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setFailure(null);
    try {
      await onSubmit(
        isSignUp
          ? { email: email.trim(), password, displayName: displayName.trim() }
          : { email: email.trim(), password },
      );
      // No success message here. The provider confirms the session with the server and
      // the dashboard replaces this screen, so the change of screen is the confirmation.
    } catch (error) {
      setFailure(isApiError(error) ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit} noValidate>
      <h1 className="disp auth-form__title">{isSignUp ? 'Create your account' : 'Sign in'}</h1>
      <p className="auth-form__subtitle">
        {isSignUp
          ? 'Your own account, with your own data. Nothing is shared unless you share it.'
          : 'Welcome back.'}
      </p>

      {failure ? (
        <p className="auth-form__failure" role="alert">
          {failure}
        </p>
      ) : null}

      {isSignUp ? (
        <Field label="Your name" required>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="input"
              type="text"
              autoComplete="name"
              value={displayName}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </Field>
      ) : null}

      <Field label="Email" required>
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            className="input"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => setEmail(event.target.value)}
          />
        )}
      </Field>

      <Field label="Password" hint={isSignUp ? PASSWORD_RULE_TEXT : undefined} required>
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            className="input"
            type="password"
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            value={password}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => setPassword(event.target.value)}
          />
        )}
      </Field>

      <button type="submit" className="btn btn--accent auth-form__submit" disabled={!canSubmit}>
        {busy ? 'Working' : isSignUp ? 'Create account' : 'Sign in'}
      </button>

      <button type="button" className="auth-form__switch" onClick={onSwitchMode}>
        {isSignUp ? 'I already have an account' : 'Create an account'}
      </button>
    </form>
  );
}
